/**
 * startPageContent.js
 *
 * Single source of truth for every string rendered on the `/start`
 * lead-generation page (see leads_clinikit_plan.md §4, §9 Phase 0).
 *
 * Rules:
 * - Pure data, no JSX.
 * - Hebrew everywhere; the only Latin words are brand/product names
 *   (CliniKit, WhatsApp, SMS, AI, PDF, Google, Morning, Grow).
 * - Price appears in exactly ONE place: FAQ #2.
 * - Professions with a `slug` reuse LANDING_PAGES copy via getProfessionContent().
 */

import { LANDING_PAGES } from "./landingPagesConfig";

// ---------------------------------------------------------------------------
// Feature flags
// ---------------------------------------------------------------------------

/**
 * When false, StartLeadForm omits `profession` / `utm` from the Firestore
 * write so the form keeps working if the extended rules (§6.2) are not yet
 * deployed. Default true — flip only for local troubleshooting.
 */
export const START_LEAD_EXTRA_FIELDS = true;

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

export const WHATSAPP = {
  number: "972543073737",
  text: "היי, הגעתי מדף /start ואשמח לשמוע על CliniKit",
};

export const getWhatsAppHref = (text = WHATSAPP.text) =>
  `https://wa.me/${WHATSAPP.number}?text=${encodeURIComponent(text)}`;

// ---------------------------------------------------------------------------
// SEO
// ---------------------------------------------------------------------------

export const SEO = {
  path: "/start",
  title: "תוכנה לניהול קליניקה וסטודיו – יומן, תשלומים ותיק מטופל | 30 יום חינם",
  description:
    "המערכת הכל-באחד הכי קלה בישראל: יומן עם כל האפשרויות, תזכורות ואישורי הגעה ב-WhatsApp, קביעת תורים עצמאית, דפי תשלום וסליקת אשראי, חשבוניות אוטומטיות, תיק מטופל, סיכומי AI וטפסים דיגיטליים. מאובטח ברמה רפואית, תמיכה אישית, מחיר משתלם. לפסיכולוגים, פיזיותרפיסטים, קלינאי תקשורת, תזונאים, קוסמטיקאיות, סטודיו גבות, ציפורניים ושיער ועוד. 30 יום חינם.",
  breadcrumbHome: "בית",
  breadcrumbCurrent: "התחלה",
  appName: "CliniKit",
  ratingValue: "4.9",
  ratingCount: "650",
  trialPrice: "0",
  monthlyPrice: "89.90",
};

// ---------------------------------------------------------------------------
// Header / footer
// ---------------------------------------------------------------------------

export const HEADER = {
  login: "כניסה",
  cta: "התחילו 30 יום חינם",
  ctaShort: "התחילו חינם",
  logoAlt: "CliniKit – תוכנה לניהול קליניקה",
};

export const FOOTER = {
  tagline: "תוכנה לניהול קליניקה למטפלים בישראל. יומן, תיק מטופל, תשלומים וטפסים — במקום אחד.",
  cta: "התחילו 30 יום חינם",
  waLabel: "דברו איתנו ב-WhatsApp",
  navTitle: "ניווט",
  nav: [
    { label: "יכולות", href: "#start-features" },
    { label: "לפי תחום", href: "#start-professions" },
    { label: "אבטחה", href: "#start-security" },
    { label: "שאלות נפוצות", href: "#start-faq" },
  ],
  legalTitle: "משפטי",
  links: [
    { label: "תנאי שימוש", href: "/terms-of-service" },
    { label: "מדיניות פרטיות", href: "/privacy-policy" },
    { label: "הצהרת נגישות", href: "/accessibility" },
  ],
  forLinksTitle: "CliniKit לפי תחום",
};

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

export const HERO = {
  overline: "תוכנה לניהול קליניקה · למטפלים · בעברית",
  h1: {
    v1: "ניהול *כל* הקליניקה שלך ממקום אחד. זמין מכל מקום.",
    a: "פחות ניהול. יותר טיפול.",
    b: "יומן שמזכיר, מאשר הגעה וגובה — בשבילכם.",
  },
  sub:
    "יומן חכם עם תזכורות ואישורי הגעה ב-WhatsApp ו-SMS, קביעת תורים עצמאית למטופלים, תיק מטופל מאובטח, סיכומי טיפול עם AI, חשבוניות וטפסים דיגיטליים — במערכת אחת שנבנתה למטפלים בישראל.",
  trust: [
    { key: "therapists", text: "+650 מטפלים כבר איתנו" },
    { key: "rating", text: "4.9/5 דירוג משתמשים" },
    { key: "moh", text: "תואם דרישות משרד הבריאות" },
  ],
};

/** Resolve the H1 variant from `?v=` (a|b); anything else → v1. */
export const getHeroTitle = (variant) =>
  HERO.h1[variant === "a" || variant === "b" ? variant : "v1"];

// ---------------------------------------------------------------------------
// Lead form
// ---------------------------------------------------------------------------

export const FORM = {
  title: "נשמח להכיר את הקליניקה שלכם",
  sub: "משאירים פרטים — ונחזור אליכם לשיחת היכרות קצרה תוך יום עסקים",
  availabilityDot: "זמינים השבוע",
  labels: {
    fullName: "שם מלא",
    phone: "נייד",
    message: "ספרו לנו בקצרה על הקליניקה והצורך שלכם",
  },
  placeholders: {
    fullName: "השם שלכם",
    message:
      "למשל: קליניקה לפיזיותרפיה עם 3 מטפלים, רוצים תזכורות אוטומטיות וחשבוניות",
  },
  optionalHint: "לא חובה, אבל עוזר לנו להגיע מוכנים",
  button: "קבעו לי שיחת היכרות חינם",
  buttonLoading: "שולח…",
  fineprint: "30 יום ניסיון חינם · ללא כרטיס אשראי · ללא ספאם",
  errors: {
    fullName: "יש להזין שם מלא",
    phone: "יש להזין מספר נייד ישראלי תקין",
    message: "עד 1000 תווים",
    tooFast: "רגע… נסו שוב",
    submit: "שליחת הטופס נכשלה. אפשר גם לכתוב לנו ב-WhatsApp",
    submitWaLink: "לשיחה ב-WhatsApp",
  },
  success: {
    title: "תודה, קיבלנו!",
    text: "נחזור אליכם לשיחת היכרות של 15 דקות תוך יום עסקים.",
    waPrompt: "מעדיפים לדבר עכשיו?",
    waButton: "שיחה ב-WhatsApp",
    tourPrompt: "בינתיים: סיור של 4 דקות במערכת",
    tourHref: "/#home-video",
  },
  limits: { fullNameMin: 2, fullNameMax: 80, messageMax: 1000 },
  dwellMs: 2000,
  dedupeHours: 24,
  storagePrefix: "ck_start_lead_",
};

/** Rotating textarea placeholder examples. Profession-aware via `{service}`. */
export const MESSAGE_PLACEHOLDER_EXAMPLES = [
  "למשל: קליניקה עם 3 מטפלים, רוצים תזכורות אוטומטיות וחשבוניות",
  "למשל: עובד/ת לבד, מחפש/ת יומן עם אישורי הגעה ב-WhatsApp ותיק מטופל",
  "למשל: מעבר ממחברת ואקסל — צריכים טפסים דיגיטליים וחתימה מהנייד",
  "למשל: {service} — רוצים שהמטופלים יקבעו תורים לבד ויקבלו קישור תשלום",
];

export const getMessagePlaceholder = (index, professionKey) => {
  const base =
    MESSAGE_PLACEHOLDER_EXAMPLES[
      ((index % MESSAGE_PLACEHOLDER_EXAMPLES.length) +
        MESSAGE_PLACEHOLDER_EXAMPLES.length) %
        MESSAGE_PLACEHOLDER_EXAMPLES.length
    ];
  const pro = getProfession(professionKey);
  const service = pro?.calendarServices?.[0] || "קליניקה טיפולית";
  return base.replace("{service}", service);
};

// ---------------------------------------------------------------------------
// Professions
// ---------------------------------------------------------------------------

/**
 * Every row from plan §1.2.
 * key              – stored on the lead as `profession`, used in `?p=`
 * slug             – /for/ page slug (LANDING_PAGES) or null
 * heroSub          – one-liner shown under the H1 when selected
 * calendarServices – 3 Hebrew service names for the calendar mockup
 * featureExamples  – per-row personalised examples {calendar, docs, payments}
 */
export const PROFESSIONS = [
  {
    key: "psychology",
    label: "פסיכולוגים",
    slug: "psychology",
    heroSub: "תיק מטופל חסוי, יומן פגישות קבועות ותזכורות שמצמצמות ביטולים.",
    calendarServices: ["טיפול פרטני", "פגישת הערכה", "טיפול זוגי"],
    featureExamples: {
      calendar: "פגישה שבועית קבועה עם תזכורת ב-WhatsApp יום לפני",
      docs: "סיכום פגישה שנכתב עם AI מתוך הנקודות שלכם",
      payments: "קישור תשלום אחרי כל פגישה וקבלה אוטומטית",
    },
  },
  {
    key: "psychotherapy",
    label: "פסיכותרפיסטים",
    slug: "psychology",
    heroSub: "סדרת טיפולים מתועדת, חסויה ונעולה — ותזכורות שעובדות בשבילכם.",
    calendarServices: ["פסיכותרפיה", "פגישת אינטייק", "טיפול קבוצתי"],
    featureExamples: {
      calendar: "סדרת פגישות שבועית עם אישור הגעה בלחיצה",
      docs: "תיעוד פגישה נעול לפי דרישות הרגולציה",
      payments: "חשבונית אוטומטית בסוף כל חודש",
    },
  },
  {
    key: "clinical_social_work",
    label: 'עו"ס קליניים',
    slug: "clinical-social-work",
    heroSub: "תיעוד מקצועי, טפסי הסכמה חתומים ויומן שמסתדר לבד.",
    calendarServices: ["טיפול פרטני", "פגישת הורים", "הדרכה"],
    featureExamples: {
      calendar: "פגישות עם משפחות ותזכורות לכל המשתתפים",
      docs: "דוח התקדמות מוכן לייצוא ב-PDF",
      payments: "קבלה דיגיטלית לכל פגישה",
    },
  },
  {
    key: "emotional_therapy",
    label: "מטפלים רגשיים ובהבעה",
    slug: "emotional-therapy",
    heroSub: "טיפול באמנות, בתנועה או בדרמה — התיעוד והיומן מסודרים, אתם פנויים ליצירה.",
    calendarServices: ["טיפול באמנות", "טיפול בתנועה", "פגישת הורים"],
    featureExamples: {
      calendar: "תזכורת להורים ב-WhatsApp ערב לפני המפגש",
      docs: "סיכום מפגש עם תמונות ותיעוד תהליך",
      payments: "גבייה חודשית מרוכזת להורים",
    },
  },
  {
    key: "speech_therapy",
    label: "קלינאי/ות תקשורת",
    slug: "speech-therapy",
    heroSub: "מעקב התקדמות, סיכומי טיפול עם AI ותזכורות להורים — בלי לילות של כתיבה.",
    calendarServices: ["טיפול תקשורת", "אבחון שפה", "הדרכת הורים"],
    featureExamples: {
      calendar: "סדרת טיפולים שבועית עם אישור הגעה של ההורים",
      docs: "סיכום טיפול לפי מטרות ומעקב התקדמות",
      payments: "חשבונית חודשית אוטומטית לכל משפחה",
    },
  },
  {
    key: "occupational_therapy",
    label: "מרפאים/ות בעיסוק",
    slug: "occupational-therapy",
    heroSub: "אבחונים, תוכניות טיפול ודוחות להורים ולמסגרות — מסודרים במקום אחד.",
    calendarServices: ["ריפוי בעיסוק", "אבחון התפתחותי", "הדרכת הורים"],
    featureExamples: {
      calendar: "חדרי טיפול ומטפלים בלי התנגשויות",
      docs: "דוח למסגרת החינוכית ב-PDF בלחיצה",
      payments: "קישור תשלום להורים אחרי כל טיפול",
    },
  },
  {
    key: "physiotherapy",
    label: "פיזיותרפיסטים",
    slug: "physiotherapy",
    heroSub: "סדרות טיפול, אישורי הגעה ותשלום בקליק — היומן מתמלא ואתם מטפלים.",
    calendarServices: ["פיזיותרפיה", "טיפול ידני", "שיקום לאחר פציעה"],
    featureExamples: {
      calendar: "סדרת 10 טיפולים עם תזכורת לפני כל אחד",
      docs: "תיעוד מדדים והתקדמות לאורך הסדרה",
      payments: "תשלום מראש על סדרה וקבלה אוטומטית",
    },
  },
  {
    key: "nutrition",
    label: "תזונאים ודיאטנים",
    slug: "nutrition",
    heroSub: "מעקב משקל ומדדים, תפריטים ותזכורות למפגשי המשך — הכול בתיק אחד.",
    calendarServices: ["ייעוץ תזונה", "מפגש מעקב", "בניית תפריט"],
    featureExamples: {
      calendar: "מפגשי מעקב דו-שבועיים עם אישור הגעה",
      docs: "תפריט ומדדים מתועדים בתיק המטופל",
      payments: "חבילת מפגשים עם קישור תשלום אחד",
    },
  },
  {
    key: "naturopathy",
    label: "נטורופתים",
    slug: "naturopathy",
    heroSub: "אינטייק מפורט, המלצות טיפול ותזכורות — בלי ניירת.",
    calendarServices: ["ייעוץ נטורופתי", "מפגש מעקב", "אבחון ראשוני"],
    featureExamples: {
      calendar: "מפגש ראשון ארוך ומפגשי מעקב קצרים ביומן אחד",
      docs: "שאלון אינטייק שהמטופל ממלא מהנייד לפני המפגש",
      payments: "קבלה דיגיטלית שנשלחת למטופל אוטומטית",
    },
  },
  {
    key: "chinese_medicine",
    label: "רפואה סינית ודיקור",
    slug: "chinese-medicine",
    heroSub: "סדרות דיקור, תיעוד טיפול ותזכורות ב-WhatsApp — פשוט ומסודר.",
    calendarServices: ["דיקור סיני", "שיאצו", "ייעוץ צמחי מרפא"],
    featureExamples: {
      calendar: "סדרת טיפולים שבועית עם אישור הגעה",
      docs: "תיעוד נקודות דיקור וממצאים בכל טיפול",
      payments: "תשלום על סדרה מראש או לפי טיפול",
    },
  },
  {
    key: "parenting_counseling",
    label: "מדריכי הורים",
    slug: "parenting-counseling",
    heroSub: "פגישות הורים, סיכומים ומשימות בין פגישות — במקום אחד שההורים אוהבים.",
    calendarServices: ["הדרכת הורים", "פגישת הורים משותפת", "ייעוץ משפחתי"],
    featureExamples: {
      calendar: "תזכורת לשני ההורים לפני כל פגישה",
      docs: "סיכום פגישה ומשימות לבית בתיק אחד",
      payments: "קישור תשלום אחרי כל פגישה",
    },
  },
  {
    key: "sleep_consulting",
    label: "יועצי שינה",
    slug: null,
    heroSub: "ליווי אינטנסיבי, מעקב יומי ותזכורות להורים — בלי לאבד אף הודעה.",
    calendarServices: ["ייעוץ שינה", "שיחת מעקב", "פגישת פתיחה"],
    featureExamples: {
      calendar: "שיחות מעקב קצרות לאורך השבוע ביומן אחד",
      docs: "תיעוד תהליך הליווי ושאלון פתיחה דיגיטלי",
      payments: "חבילת ליווי עם תשלום אחד מראש",
    },
  },
  {
    key: "couples_counseling",
    label: "יועצים זוגיים ומיניים",
    slug: "couples-counseling",
    heroSub: "דיסקרטיות מלאה, תיק משותף לזוג ותזכורות שמגיעות לשניהם.",
    calendarServices: ["ייעוץ זוגי", "טיפול מיני", "פגישה פרטנית"],
    featureExamples: {
      calendar: "תזכורת לשני בני הזוג ואישור הגעה בלחיצה",
      docs: "תיעוד חסוי ונעול לפי דרישות הרגולציה",
      payments: "חשבונית אחת לזוג בסוף כל חודש",
    },
  },
  {
    key: "personal_coaching",
    label: "מאמנים אישיים",
    slug: "personal-coaching",
    heroSub: "תהליכי אימון, מטרות ומשימות — והיומן שמתמלא לבד מהדף האישי שלכם.",
    calendarServices: ["אימון אישי", "אימון עסקי", "שיחת היכרות"],
    featureExamples: {
      calendar: "לקוחות קובעים שיחה לבד מהדף האישי שלכם",
      docs: "מטרות ומשימות מתועדות בין פגישה לפגישה",
      payments: "חבילת אימון עם קישור תשלום וקבלה",
    },
  },
  {
    key: "hypnotherapy",
    label: "היפנותרפיסטים",
    slug: null,
    heroSub: "מפגשים ממוקדים, תיעוד תהליך ותזכורות — בלי להתעסק בניהול.",
    calendarServices: ["היפנותרפיה", "מפגש NLP", "פגישת היכרות"],
    featureExamples: {
      calendar: "מפגשים ארוכים ללא התנגשויות בין חדרים",
      docs: "סיכום מפגש והמשך תהליך בתיק הלקוח",
      payments: "קישור תשלום מאובטח אחרי כל מפגש",
    },
  },
  {
    key: "beauty_therapy",
    label: "קוסמטיקאיות ומטפלות יופי",
    slug: null,
    heroSub: "תורים שמתמלאים לבד, אישורי הגעה ב-WhatsApp ותיק לקוחה עם כל ההיסטוריה.",
    calendarServices: ["טיפול פנים", "טיפול קוסמטי", "ייעוץ עור"],
    featureExamples: {
      calendar: "לקוחות קובעות תור לבד ומאשרות הגעה בלחיצה",
      docs: "הצהרת בריאות וטופס הסכמה חתומים מהנייד",
      payments: "תשלום בקליק וקבלה אוטומטית",
    },
  },
  {
    key: "reflexology_massage",
    label: "רפלקסולוגים ומעסים",
    slug: null,
    heroSub: "יומן מלא, פחות הברזות, ותשלום שמגיע לפני שהמטופל יוצא מהדלת.",
    calendarServices: ["רפלקסולוגיה", "עיסוי רפואי", "עיסוי שוודי"],
    featureExamples: {
      calendar: "תזכורת ואישור הגעה שעתיים לפני הטיפול",
      docs: "הצהרת בריאות דיגיטלית לפני הטיפול הראשון",
      payments: "קישור תשלום בסיום הטיפול",
    },
  },
  {
    key: "child_development",
    label: "מכוני התפתחות הילד",
    slug: null,
    heroSub: "כמה מטפלים, כמה חדרים, הרבה הורים — ומערכת אחת שמחזיקה את הכול.",
    calendarServices: ["ריפוי בעיסוק", "טיפול תקשורת", "פיזיותרפיה ילדים"],
    featureExamples: {
      calendar: "מטפלים וחדרים ללא התנגשויות, תזכורות להורים",
      docs: "דוחות התקדמות למסגרות ולהורים ב-PDF",
      payments: "גבייה מרוכזת וחשבוניות אוטומטיות",
    },
  },
  {
    key: "multidisciplinary",
    label: "קליניקות רב-תחומיות",
    slug: "clinic-management",
    heroSub: "צוות, הרשאות, מזכירות ולוח גבייה — שליטה מלאה על מרפאה עם כמה מטפלים.",
    calendarServices: ["פיזיותרפיה", "ייעוץ תזונה", "טיפול רגשי"],
    featureExamples: {
      calendar: "יומן לכל מטפל, הרשאות לכל תפקיד",
      docs: "תיקי מטופלים משותפים לפי הרשאה",
      payments: "לוח גבייה אחד לכל הקליניקה",
    },
  },
  {
    key: "holistic",
    label: "רפואה משלימה",
    slug: "naturopathy",
    heroSub: "כל שיטות הטיפול שלכם ביומן אחד — עם תזכורות, תיק מטופל ותשלום בקליק.",
    calendarServices: ["רייקי", "הילינג", "ייעוץ הוליסטי"],
    featureExamples: {
      calendar: "סוגי טיפול שונים בצבעים שונים ביומן אחד",
      docs: "תיעוד טיפול ושאלון אינטייק דיגיטלי",
      payments: "קבלה דיגיטלית לכל טיפול",
    },
  },
];

export const PROFESSIONS_STRIP = {
  overline: "מתאים לכל סוגי הקליניקות",
  fallback: "לא מצאתם את התחום שלכם? CliniKit גמישה לכל קליניקה — ספרו לנו בטופס.",
  groupLabel: "בחירת תחום טיפול",
};

const PROFESSION_BY_KEY = Object.fromEntries(PROFESSIONS.map((p) => [p.key, p]));

/** Lookup by `key` or by `/for/` slug (first match). Returns undefined if unknown. */
export const getProfession = (keyOrSlug) => {
  if (!keyOrSlug) return undefined;
  return (
    PROFESSION_BY_KEY[keyOrSlug] ||
    PROFESSIONS.find((p) => p.slug === keyOrSlug)
  );
};

/**
 * Personalised copy for a profession. Pulls heroTitle/heroSubtitle/
 * specializations from LANDING_PAGES when a slug exists there, so copy is
 * never duplicated. Always returns usable defaults.
 */
export const getProfessionContent = (keyOrSlug) => {
  const pro = getProfession(keyOrSlug);
  const landing = pro?.slug
    ? Object.values(LANDING_PAGES).find((page) => page.slug === pro.slug)
    : undefined;
  return {
    key: pro?.key || null,
    label: pro?.label || null,
    slug: pro?.slug || null,
    heroSub: pro?.heroSub || HERO.sub,
    landingTitle: landing?.heroTitle || null,
    landingSubtitle: landing?.heroSubtitle || null,
    specializations: landing?.specializations || [],
    calendarServices: pro?.calendarServices || DEFAULT_CALENDAR_SERVICES,
    featureExamples: pro?.featureExamples || DEFAULT_FEATURE_EXAMPLES,
    forHref: pro?.slug ? `/for/${pro.slug}` : null,
  };
};

export const DEFAULT_CALENDAR_SERVICES = ["טיפול", "פגישת ייעוץ", "מפגש מעקב"];

export const DEFAULT_FEATURE_EXAMPLES = {
  calendar: "תזכורת ב-WhatsApp יום לפני ואישור הגעה בלחיצה",
  docs: "סיכום טיפול שנכתב עם AI מתוך הנקודות שלכם",
  payments: "קישור תשלום למטופל וחשבונית אוטומטית",
};

// ---------------------------------------------------------------------------
// Mockup
// ---------------------------------------------------------------------------

export const SUPPORT_MOCKUP = {
  botName: "לני · העוזר של CliniKit",
  online: "מחובר",
  question: "איך שולחים טופס הסכמה למטופל?",
  answer: "נכנסים לתיק המטופל › «טפסים» › «שלח לחתימה». המטופל מקבל קישור ב-WhatsApp וחותם מהנייד.",
  humanPrompt: "צריכים אדם?",
  human: "צוות התמיכה זמין ב-WhatsApp",
};

export const MOCKUP = {
  patientNames: ["דנה לוי", "יוסי כהן", "נועה ברק", "אורי שמש", "מאיה פרץ", "איתי גל", "רוני אשכנזי", "תמר אדר"],
  days: ["א׳", "ב׳", "ג׳", "ד׳", "ה׳"],
  waBubble: "היי דנה, תזכורת לפגישה מחר ב-10:00 — לאישור הגעה לחצו כאן",
  waConfirmed: "אישרתי הגעה",
  todayLabel: "היום",
  confirmedChip: "אישר/ה הגעה",
  cancelledChip: "ביטל/ה",
};

// ---------------------------------------------------------------------------
// Feature rows (§4.3)
// ---------------------------------------------------------------------------

export const FEATURE_CTA = "התחילו 30 יום חינם";

export const FEATURES_SECTION = {
  overline: "מה בפנים",
  title: "כל מה שהקליניקה צריכה. במקום אחד.",
  sub: "לא עוד חמש אפליקציות, קבוצות WhatsApp וגיליונות אקסל. מערכת אחת שעושה את העבודה — ואתם חוזרים לטפל.",
};

export const FEATURES = [
  {
    key: "calendar",
    icon: "CalendarCheck",
    title: "ניהול יומן מתקדם",
    tone: "blue",
    bullets: [
      "הודעת קביעת תור ותזכורת אוטומטית ב-WhatsApp / SMS",
      "אישורי הגעה למטופלים בלחיצה אחת",
      "קביעת תורים עצמאית מדף אישי",
      "סנכרון דו-כיווני ליומן Google",
      "ניהול חדרים וריבוי מטפלים ללא התנגשויות",
    ],
    pop: { icon: "WhatsappLogo", text: "דנה לוי אישרה הגעה · 09:00" },
    mockup: "confirmations",
    exampleKey: "calendar",
  },
  {
    key: "payments",
    icon: "CreditCard",
    title: "גבייה וחשבוניות אוטומטיות",
    tone: "yellow",
    bullets: [
      "תמיכה בדפי תשלום מאובטחים",
      "התממשקות לשירותי סליקה",
      "התממשקות לשירותי הפקת חשבוניות",
      "תזכורות תשלום אוטומטיות ולא אוטומטיות למטופלים",
      "לוח גבייה: מה שולם ומה ממתין",
      "דוחות כספיים מפורטים",
    ],
    pop: { icon: "CreditCard", text: "התשלום התקבל · חשבונית נשלחה" },
    mockup: "kanban",
    exampleKey: "payments",
  },
  {
    key: "docs",
    icon: "Notebook",
    title: "תיק מטופל וסיכומי טיפול עם AI",
    tone: "mint",
    bullets: [
      "סיכום טיפול נכתב עם AI מתוך הנקודות שלכם",
      "התייעצות עם AI לגבי אופציות להמשך טיפול",
      "סיכומי טיפול מתומצתים לתצוגה נוחה של היסטוריית הטיפולים",
      "סיכום סדרת טיפולים בעזרת AI",
      "ייצוא תיק / דוח ב-PDF בלחיצה",
    ],
    pop: { icon: "Sparkle", text: "סיכום AI מוכן · 12 שניות" },
    mockup: "treatmentDoc",
    exampleKey: "docs",
  },
  {
    key: "forms",
    icon: "Signature",
    title: "טפסים, שאלונים וחתימה דיגיטלית מהנייד",
    tone: "purple",
    bullets: [
      "שליחת טופס או שאלון למטופל בלחיצה אחת",
      "חתימה מהנייד עם אימות זהות",
      "אתם בונים את הטפסים והשאלונים שלכם!",
      "המטופל מחזיר את המסמך חתום — והוא מתויק אוטומטית בכרטיס המטופל",
      "נשמר אוטומטית — בלי נייר, בלי סריקות",
    ],
    pop: { icon: "Signature", text: "טופס הסכמה נחתם מהנייד" },
    mockup: "signature",
    exampleKey: null,
  },
  {
    key: "team",
    icon: "UsersThree",
    title: "ניהול צוות ומרפאה",
    tone: "pink",
    bullets: [
      "הרשאות מפורטות לכל תפקיד",
      "מזכירות שרואה יומן — לא תיקים רפואיים",
      "משימות ותזכורות פנימיות",
      "מסך «מה ממתין לכם היום»",
      "יומן פעילות מלא לכל פעולה",
    ],
    pop: { icon: "CheckSquare", text: "3 משימות נסגרו היום" },
    mockup: "pending",
    exampleKey: null,
  },
  {
    key: "support",
    icon: "ChatCircleDots",
    title: "תמיכה צמודה — אנושית וגם AI",
    tone: "mint",
    note: "אנחנו קרובים ללקוחות שלנו. באמת.",
    bullets: [
      "לני — עוזר AI בתוך המערכת, עונה בעברית על כל שאלה תפעולית וטכנית, 24/7",
      "נציג תמיכה אנושי זמין לכם ב-WhatsApp",
      "ליווי אישי בהקמה ובהעברת הנתונים מהמערכת הקודמת",
      "מדריכים, סיורים מודרכים ועדכונים שוטפים",
      "שומעים אתכם: פיצ'רים חדשים נבנים מבקשות של מטפלים",
    ],
    pop: { icon: "ChatCircleDots", text: "לני ענה · 4 שניות" },
    mockup: "support",
    exampleKey: null,
  },
];

// ---------------------------------------------------------------------------
// Stats (§4.4) — numbers mirror HomeStats.js; count derives from LANDING_PAGES
// ---------------------------------------------------------------------------

export const STATS_SECTION = { title: "מטפלים כבר סומכים עלינו. בכל יום." };
export const STATS = [
  { key: "therapists", value: "650", prefix: "+", label: "מטפלים" },
  { key: "rating", value: "4.9", suffix: "/5", label: "דירוג משתמשים", stars: true },
  { key: "trial", value: "30", suffix: " יום", label: "ניסיון חינם" },
];

// ---------------------------------------------------------------------------
// Testimonials (§4.5) — section copy only; quotes imported from HomeTestimonials
// ---------------------------------------------------------------------------

export const TESTIMONIALS_SECTION = {
  overline: "מטפלים מספרים",
  title: "למה מטפלים נשארים עם CliniKit",
};

// ---------------------------------------------------------------------------
// Security (§4.6)
// ---------------------------------------------------------------------------

export const SECURITY = {
  overline: "אבטחה ורגולציה",
  title: "המידע של המטופלים שלכם מוגן. כמו שצריך.",
  pillars: [
    { icon: "LockKey", title: "הצפנה מקצה לקצה", text: "כל המידע מוצפן בתקן AES-256 במנוחה ובתעבורה." },
    { icon: "Cloud", title: "תשתית Google Cloud", text: "שרתים מאובטחים עם זמינות גבוהה וגיבוי יומי." },
    { icon: "DeviceMobile", title: "כניסה מאומתת בנייד", text: "קוד חד-פעמי לנייד בכל כניסה — בלי סיסמאות שנשכחות." },
    { icon: "ShieldCheck", title: "תואם דרישות משרד הבריאות", text: "אימות זהות בכל קישור למטופל ותיעוד שלא נמחק." },
    { icon: "Database", title: "מאגר מידע רשום", text: "עובדים לפי חוק הגנת הפרטיות ותקנות אבטחת המידע." },
    { icon: "UserCircle", title: "המידע שלכם. תמיד.", text: "ייצוא מלא בכל רגע, ללא נעילה וללא שאלות." },
  ],
  checks: ["ללא גישה לצד שלישי", "רשומות רפואיות לא נמחקות", "גיבוי יומי אוטומטי"],
};

// ---------------------------------------------------------------------------
// How it works (§4.7)
// ---------------------------------------------------------------------------

export const STEPS = {
  overline: "איך זה עובד",
  title: "שלושה צעדים. והקליניקה רצה.",
  items: [
    { num: "1", title: "משאירים פרטים", text: "30 שניות. שם, נייד ומשפט על הקליניקה." },
    { num: "2", title: "שיחת היכרות של 15 דקות", text: "מכירים את הקליניקה ומקימים אתכם יחד." },
    { num: "3", title: "עובדים", text: "30 יום חינם, ללא כרטיס אשראי, ביטול בכל עת." },
  ],
};

// ---------------------------------------------------------------------------
// FAQ (§4.8) — 8 items, mirrored 1:1 by the FAQPage JSON-LD
// ---------------------------------------------------------------------------

export const FAQ_SECTION = {
  overline: "שאלות נפוצות",
  title: "כל מה שרציתם לדעת לפני שמשאירים פרטים",
};

export const FAQ = [
  {
    question: "למי מתאימה CliniKit?",
    answer:
      'לכל קליניקה וסטודיו שעובדים בתורים: פסיכולוגים, פסיכותרפיסטים, עו"ס קליניים, קלינאי תקשורת, מרפאים בעיסוק, פיזיותרפיסטים, תזונאים, נטורופתים, רפואה סינית, מדריכי הורים, יועצי שינה, מאמנים, רופאים — וגם קוסמטיקאיות ומטפלות פנים, סטודיו לעיצוב גבות וריסים, סטודיו ציפורניים, מספרות וסטודיו לשיער, לייזר, עיסוי וספא. לבד או עם צוות.',
  },
  {
    question: "כמה זה עולה?",
    answer:
      'אחרי 30 ימי הניסיון: 39.90 ₪ לחודש במסלול המתחילים, ו-89.90 ₪ לחודש למשתמש במסלול המקצועי, לא כולל מע"מ. ביטול בכל עת, ללא דמי ביטול.',
  },
  {
    question: "מה כולל הניסיון?",
    answer:
      "גישה מלאה למסלול המקצועי למשך 30 יום — יומן, תיק מטופל, סיכומי AI, טפסים ותזכורות — עד 15 מטופלים ו-20 הודעות SMS. ללא כרטיס אשראי.",
  },
  {
    question: "איך עובדות התזכורות ואישורי ההגעה?",
    answer:
      "המטופל מקבל תזכורת ב-WhatsApp או SMS 24 שעות ושעתיים לפני הפגישה, עם קישור לאישור הגעה בלחיצה. אתם רואים ביומן מי אישר ומי ביטל, והזמנים ניתנים להתאמה.",
  },
  {
    question: "האם המטופלים יכולים לקבוע תורים לבד?",
    answer:
      "כן. לכל קליניקה יש דף קביעה אישי שבו המטופל בוחר שעה פנויה ומאמת את זהותו בקוד לנייד. אתם מאשרים ידנית או מגדירים אישור אוטומטי.",
  },
  {
    question: "האם המידע מאובטח ועומד בדרישות משרד הבריאות?",
    answer:
      "כן. המידע מוצפן בתקן AES-256 על תשתית Google Cloud, כל קישור למטופל מחייב אימות זהות, ורשומות רפואיות אינן נמחקות — בהתאם לדרישות משרד הבריאות וחוק הגנת הפרטיות.",
  },
  {
    question: "יש אפליקציה לנייד?",
    answer:
      "המערכת מותאמת 100% לנייד: מוסיפים אותה למסך הבית בלחיצה ומקבלים התראות Push על פגישות, אישורי הגעה ותשלומים — בלי להוריד כלום מהחנות.",
  },
  {
    question: "האם אפשר לגבות תשלומים ולסלוק אשראי דרך המערכת?",
    answer:
      "כן. דפי תשלום מאובטחים נשלחים למטופל ב-WhatsApp או SMS, הכרטיס נסלק אונליין, והקבלה או החשבונית מופקות אוטומטית (Morning / חשבונית ירוקה, Grow). לוח הגבייה מראה מה שולם ומה ממתין, ותזכורות תשלום יוצאות לבד.",
  },
  {
    question: "איזו תמיכה מקבלים?",
    answer:
      "נציג אנושי ב-WhatsApp, ליווי אישי בהקמה ובהעברת הנתונים מהמערכת הקודמת או מאקסל, ולני — עוזר AI בתוך המערכת שעונה בעברית 24/7. אבטחת המידע באחריותנו וכלולה במחיר, כמו ביטוח — אתם מתפנים לטפל.",
  },
  {
    question: "מה קורה אחרי שאני משאיר/ה פרטים?",
    answer:
      "נחזור אליכם תוך יום עסקים לשיחת היכרות של 15 דקות, נקים יחד את הקליניקה במערכת — ותתחילו לעבוד באותו יום.",
  },
];

// ---------------------------------------------------------------------------
// Closing form (§4.9) & sticky bar (§4.11)
// ---------------------------------------------------------------------------

export const CLOSE = {
  overline: "מתחילים היום",
  title: "מוכנים שהקליניקה תתנהל לבד?",
  sub: "השאירו פרטים — נחזור אליכם לשיחת היכרות של 15 דקות. 30 יום חינם, בלי כרטיס אשראי, בלי התחייבות.",
};

export const STICKY = {
  primary: "השאירו פרטים",
  waAria: "שיחה ב-WhatsApp",
};
